import {type Action, createReducer, on} from '@ngrx/store'
import type {Action as ActionModel} from '@models/action'
import type {Assortments} from '@models/assortments'
import type {ClassifierSection} from '@models/classifier'
import type {Distributor} from '@models/distributor'
import type {Order} from '@models/order/order'
import type {Outlet} from '@models/outlet'
import type {OutletRelationCode} from '@models/outletRelationCode'
import type {SalesInfo} from '@models/sales'
import {OutletService} from '@service/outlet/outlet.service'
import * as ActivitiesActions from './actitivies/activities.actions'
import * as OrderActions from './orders/orders.actions'
import * as OutletDetailActions from './outlet-details.actions'

export type ActivityReason = {
  id: number
  label: string
  isEffective: boolean
}

export type OutletDetailsState = {
  outlet: Outlet
  outletCodes: OutletRelationCode[]
  collapsed: boolean
  displayActionsList: boolean
  sales: SalesInfo
  distributors: Distributor[]
  objectives: ActivityReason[]
  assortments: Assortments[]
  from: string
  orderInfo: {
    distributors: Distributor[]
    codes: OutletRelationCode[]
    action: ActionModel
    order: Order
    outlet: Outlet
    productsAssortments: {
      excludeAssortments: any[]
      saleAssortments: any[]
    }
  }
}

const initialState: OutletDetailsState = {
  outlet: null,
  outletCodes: [],
  collapsed: true,
  displayActionsList: false,
  sales: null,
  distributors: [],
  objectives: [],
  assortments: [],
  from: '',
  orderInfo: {
    distributors: null,
    codes: null,
    action: null,
    order: null,
    outlet: null,
    productsAssortments: {
      excludeAssortments: [],
      saleAssortments: [],
    },
  },
}

const reducer = createReducer(
  initialState,
  on(OutletDetailActions.getOutletDetailsResponse, (state, {outlet, classifiers, objectives}) => {
    outlet = {
      ...outlet,
      classifiersParsed: OutletService.parseClassifiers(outlet.classifiers, classifiers),
      links: parseClassifierLinks(outlet.classifiers, classifiers),
    }
    const parsedActivityObjectives = parseActivityObjectives(objectives, state.objectives)
    return {
      ...state,
      outlet,
      objectives: parsedActivityObjectives,
    }
  }),
  on(ActivitiesActions.endActivityResponse, (state, {outlet}) => ({
    ...state,
    outlet,
  })),
  on(OutletDetailActions.updateOutletDetail, (state, {outlet}) => ({
    ...state,
    outlet,
  })),
  on(OutletDetailActions.setCollapsed, (state, {collapsed}) => ({
    ...state,
    collapsed,
  })),
  on(OutletDetailActions.resetOutletDetails, () => initialState),
  on(OutletDetailActions.getSalesResponse, (state, {sales}) => ({
    ...state,
    sales: {...sales},
  })),
  on(OutletDetailActions.getDistributorsResponse, (state, {distributors}) => ({
    ...state,
    distributors: [...distributors],
  })),
  on(OutletDetailActions.displayOutletActionsResponse, (state, {value}) => ({
    ...state,
    displayActionsList: value,
  })),
  on(OutletDetailActions.updateOutletResponse, (state, {outlet}) => ({
    ...state,
    outlet,
  })),
  on(
    OutletDetailActions.getOutletOrderInfoResponse,
    (state, {codes, distributors, action, order, outlet, productsAssortments}) => ({
      ...state,
      orderInfo: {
        ...state.orderInfo,
        codes,
        outlet,
        distributors,
        action,
        order,
        productsAssortments,
      },
    })
  ),
  on(OrderActions.resetOrders, OrderActions.resetOrderInProgress, state => ({
    ...state,
    orderInfo: {
      distributors: null,
      codes: null,
      action: null,
      order: null,
      outlet: null,
      productsAssortments: {
        excludeAssortments: [],
        saleAssortments: [],
      },
    },
  }))
)

export const outletDetailsReducer = (state: OutletDetailsState | undefined, action: Action) => reducer(state, action)

const parseClassifierLinks = (
  outletClassifiers: {[prop: string]: string | number},
  classifierSections: ClassifierSection[]
): {label: string; value: string; type: string}[] => {
  const links = []
  for (const [item, value] of Object.entries(outletClassifiers)) {
    const link = OutletService.getLink(value)
    if (link) {
      let label = null
      const found = classifierSections.some(s => {
        const classifier = s.classifiers.find(c => c.id === item)
        if (classifier) {
          label = classifier.label
        }
        return classifier
      })
      if (found) {
        links.push({
          label,
          ...link,
        })
      }
    }
  }
  return links
}

const parseActivityObjectives = (activityObjectives, stateActivityObjectives) => {
  if (activityObjectives && activityObjectives.length > 0) {
    return activityObjectives
  }
  if (stateActivityObjectives && stateActivityObjectives.length > 0) {
    return stateActivityObjectives
  }
  return []
}
